import './HandleAccessModal.scss';
import { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import TabNav from '../../../Components/TabNav/TabNav';
import DataPanel from '../../../Components/DataPanel/DataPanel';
import Searchbar from '../../../Components/Searchbar/Searchbar';
import XpektorButton from '../../../Components/XpektorButton/XpektorButton';
import LoadingBar from '../../../Components/LoadingBar/LoadingBar';

const gsT = (t, key) => t(`shared${key}`);

/* eslint-disable react/prop-types */
function AccessEntry({ name, onClick, type, disabled, t }) {
  return (
    <div className="member-entry">
      <p>{name}</p>
      <button
        type="button"
        className={type === 'remove' ? 'remove-button' : 'add-button'}
        onClick={onClick}
        disabled={disabled}
      >
        {type === 'remove' ? gsT(t, '.actions.remove') : gsT(t, '.actions.add')}
      </button>
    </div>
  );
}

// data = array of objects
// object =
// {
//   type: string (can be 'users', 'groups', or 'clients'),
//   all: object[] (objects with 'name' and 'id'),
//   members: string[] (array of ids)
// }
export default function HandleAccessModal({
  modalTitle,
  memberPanelTitle,
  handleClose,
  loading,
  onSave,
  data,
  t,
}) {
  const [selectedType, setSelectedType] = useState('');
  const [tabIndex, setTabIndex] = useState(0);

  const [addableData, setAddableData] = useState([]);
  const [selectedMemberData, setSelectedMemberData] = useState([]);
  const [originalMemberData, setOriginalMemberData] = useState([]);

  const [differingTypes, setDifferingTypes] = useState([]);
  const [saving, setSaving] = useState(false);

  function OnMemberChange(newSelectedMemberData) {
    setSelectedMemberData(newSelectedMemberData);

    const newMemberCount = newSelectedMemberData[selectedType].filter(
      (newMember) => !originalMemberData[selectedType].includes(newMember.id)
    ).length;

    let removedMemberCount = 0;
    for (let i = 0; i < originalMemberData[selectedType].length; i += 1) {
      if (
        !newSelectedMemberData[selectedType].some(
          (newMember) => newMember.id === originalMemberData[selectedType][i]
        )
      )
        removedMemberCount += 1;
    }

    const differs = newMemberCount > 0 || removedMemberCount > 0;
    if (differs) {
      if (!differingTypes.includes(selectedType))
        setDifferingTypes([...differingTypes, selectedType]);
    } else {
      setDifferingTypes(
        [...differingTypes].filter((type) => type !== selectedType)
      );
    }
  }

  function RemoveMember(member) {
    const newAddableData = { ...addableData };
    newAddableData[selectedType] = [...newAddableData[selectedType], member];
    setAddableData(newAddableData);

    const newSelectedMemberData = { ...selectedMemberData };
    newSelectedMemberData[selectedType] = [
      ...newSelectedMemberData[selectedType],
    ].filter((filterMember) => filterMember.id !== member.id);
    OnMemberChange(newSelectedMemberData);
  }

  function AddMember(member) {
    const newAddableData = { ...addableData };
    newAddableData[selectedType] = [...newAddableData[selectedType]].filter(
      (filterMember) => filterMember.id !== member.id
    );
    setAddableData(newAddableData);

    const newSelectedMemberData = { ...selectedMemberData };
    newSelectedMemberData[selectedType] = [
      ...newSelectedMemberData[selectedType],
      member,
    ];
    OnMemberChange(newSelectedMemberData);
  }

  useEffect(() => {
    if (loading || !data || Object.keys(data).length === 0) return;

    const newAddableData = [];
    const newSelectedMemberData = [];
    const newOrginalMemberData = [];
    data.forEach((dataEntry) => {
      newAddableData[dataEntry.type] = dataEntry.all.filter(
        (entry) => !dataEntry.members.includes(entry.id)
      );
      newSelectedMemberData[dataEntry.type] = dataEntry.all.filter((entry) =>
        dataEntry.members.includes(entry.id)
      );
      newOrginalMemberData[dataEntry.type] = dataEntry.members;
    });
    setSelectedType(data[0].type);
    setAddableData(newAddableData);
    setSelectedMemberData(newSelectedMemberData);
    setOriginalMemberData(newOrginalMemberData);
  }, [data, loading]);

  const [searchTerm, setSearchTerm] = useState('');
  const [filteredAddable, setFilteredAddable] = useState([]);
  useEffect(() => {
    if (selectedType.length === 0 || Object.keys(addableData).length === 0)
      return;

    let newFilteredAddable = [...addableData[selectedType]].sort((a, b) => {
      const stringA = a.name.toUpperCase();
      const stringB = b.name.toUpperCase();
      return stringA < stringB ? -1 : stringA > stringB ? 1 : 0;
    });
    if (searchTerm.length > 0) {
      newFilteredAddable = newFilteredAddable.filter((addable) =>
        addable.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    setFilteredAddable(newFilteredAddable);
  }, [addableData, searchTerm, selectedType]);

  useEffect(() => {
    setSearchTerm('');
  }, [selectedType]);

  async function SaveChanges() {
    if (!onSave) return;

    setSaving(true);

    const changes = [];
    differingTypes.forEach((differingType) => {
      const addedIds = selectedMemberData[differingType]
        .filter(
          (selectedMember) =>
            !originalMemberData[differingType].includes(selectedMember.id)
        )
        .map((addedMember) => addedMember.id);

      const removedIds = [];
      const originalMemberIds = originalMemberData[differingType];
      for (let i = 0; i < originalMemberIds.length; i += 1) {
        if (
          !selectedMemberData[differingType].some(
            (newMember) => newMember.id === originalMemberIds[i]
          )
        )
          removedIds.push(originalMemberIds[i]);
      }

      changes[differingType] = { addedIds, removedIds };
    });

    await onSave(changes)
      .then(() => {
        handleClose();
      })
      .catch(() => {})
      .finally(() => {
        setSaving(false);
      });
  }

  return (
    <Modal
      show
      onHide={handleClose}
      backdrop="static"
      centered
      keyboard={false}
      className="border-0 xpektor-bootstrap-modal handle-access-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>{modalTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <LoadingBar active />
        ) : (
          <div className="handle-access-content">
            <div className="item-group">
              {data && data.length > 1 ? (
                <TabNav activeIndex={tabIndex}>
                  {data.map((dataSet, index) => {
                    return (
                      <TabNav.Tab
                        key={dataSet.type}
                        title={
                          /* eslint-disable */
                          dataSet.type === 'users'
                            ? gsT(t, '.labels.users')
                            : dataSet.type === 'groups'
                            ? gsT(t, '.labels.groups')
                            : dataSet.type === 'clients'
                            ? gsT(t, '.labels.clients')
                            : gsT('.labels.not-available')
                          /* eslint-enable */
                        }
                        active={tabIndex === index}
                        onClick={() => {
                          setSelectedType(dataSet.type);
                          setTabIndex(index);
                        }}
                      />
                    );
                  })}
                </TabNav>
              ) : (
                ''
              )}
              <DataPanel
                title={`${memberPanelTitle ?? ''} (${
                  selectedMemberData[selectedType]?.length ?? 0
                })`}
                titleSize="s"
                minHeight="210px"
                maxHeight="210px"
                scroll
              >
                <div className="member-list">
                  {selectedMemberData[selectedType]?.length > 0 ? (
                    selectedMemberData[selectedType]
                      .sort((a, b) => {
                        const stringA = a.name.toUpperCase();
                        const stringB = b.name.toUpperCase();
                        return stringA < stringB
                          ? -1
                          : stringA > stringB
                          ? 1
                          : 0;
                      })
                      .map((member) => {
                        return (
                          <AccessEntry
                            key={member.id}
                            name={member.name}
                            type="remove"
                            disabled={saving}
                            onClick={() => {
                              RemoveMember(member);
                            }}
                            t={t}
                          />
                        );
                      })
                  ) : (
                    <p className="no-members-entry">
                      {/* eslint-disable */}
                      {t(
                        'modal.handle-access.no-members-entry-prefix.none-exists'
                      )}
                      &nbsp;
                      {selectedType === 'users'
                        ? gsT(t, '.labels.users').toLowerCase()
                        : selectedType === 'groups'
                        ? gsT(t, '.labels.groups').toLowerCase()
                        : selectedType === 'clients'
                        ? gsT(t, '.labels.clients').toLowerCase()
                        : ''}
                      {/* eslint-enable */}
                    </p>
                  )}
                </div>
              </DataPanel>
            </div>
            <div className="item-group">
              <Searchbar
                placeholder={`${gsT(t, '.actions.search')} ${
                  selectedType === 'users'
                    ? gsT(t, '.labels.users').toLowerCase()
                    : selectedType === 'groups'
                    ? gsT(t, '.labels.groups').toLowerCase()
                    : selectedType === 'clients'
                    ? gsT(t, '.labels.clients').toLowerCase()
                    : ''
                }`}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                }}
              />
              <DataPanel minHeight="200px" maxHeight="200px" scroll>
                <div className="member-list">
                  {filteredAddable.length > 0 ? (
                    filteredAddable.map((addable) => {
                      return (
                        <AccessEntry
                          key={addable.id}
                          name={addable.name}
                          type="add"
                          disabled={saving}
                          onClick={() => {
                            AddMember(addable);
                          }}
                          t={t}
                        />
                      );
                    })
                  ) : (
                    <p className="no-members-entry">
                      {/* eslint-disable */}
                      {t(
                        'modal.handle-access.no-members-entry-prefix.none-exists-other'
                      )}
                      &nbsp;
                      {selectedType === 'users'
                        ? gsT(t, '.labels.users').toLowerCase()
                        : selectedType === 'groups'
                        ? gsT(t, '.labels.groups').toLowerCase()
                        : selectedType === 'clients'
                        ? gsT(t, '.labels.clients').toLowerCase()
                        : ''}
                      {/* eslint-enable */}
                    </p>
                  )}
                </div>
              </DataPanel>
            </div>
            {differingTypes.length > 0 ? (
              <div className="confirm-changes-row">
                <p>{t('modal.handle-access.unsaved-changes')}</p>
                <XpektorButton
                  content={gsT(t, '.actions.save')}
                  loading={saving}
                  onClick={() => {
                    SaveChanges();
                  }}
                />
              </div>
            ) : (
              ''
            )}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}
